/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Aegis AO Soft LLC and Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft LLC
 *
 * Storefront design (myezwheels Design page): branding/design/content/sections + AI translation.
 */

import axios from 'axios';
import apiClient from './client';

export interface StorefrontDesign {
  ownerId?: string;
  subdomain?: string | null;
  customDomain?: string | null;
  displayName?: string | null;
  // Design tab
  logoUrl?: string | null;
  faviconUrl?: string | null;
  bannerImageUrl?: string | null;
  backgroundImageUrl?: string | null;
  videoUrl?: string | null;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  accentColor?: string | null;
  tagLine?: string | null;
  aboutText?: string | null;
  // About + Terms hold rich text (HTML); edited with the RichTextEditor, rendered formatted on the storefront.
  terms?: string | null;
  // Multilingual variants (JSON localized maps) of the Design-tab text fields.
  tagLineJson?: string | null;
  aboutTextJson?: string | null;
  termsJson?: string | null;
  contactAddressJson?: string | null;
  // Contact block (shown first on the storefront Contact page)
  contactEmail?: string | null;
  contactPhone?: string | null;
  contactAddress?: string | null;
  // Content tab
  contentJson?: string | null;
  customCss?: string | null;
  // Sections tab
  sectionsJson?: string | null;
  // Main tab — how the storefront lists the fleet: one card per make+model, every car, or
  // 'both' (the visitor switches, the historical behaviour).
  vehicleDisplayMode?: 'models' | 'cars' | 'both' | null;
  // Languages this storefront has been translated into (read-only; maintained by the translation job).
  translatedLanguages?: string[] | null;
}

/** Progress of the storefront auto-translation background job. */
export interface TranslationJobStatus {
  running: boolean;
  total: number;
  done: number;
  percent: number;
  error?: string | null;
}

export const designApi = {
  /** Load the owner's storefront design (branding + design + content + sections). */
  getDesign: async (ownerId?: string): Promise<StorefrontDesign> => {
    const r = await apiClient.get<{ result: StorefrontDesign }>('/owner-site', {
      params: ownerId ? { ownerId } : undefined,
    });
    return r.data?.result ?? {};
  },

  /** Save the storefront design. Image/video fields hold blob URLs (see uploadImage). */
  saveDesign: async (design: StorefrontDesign, ownerId?: string): Promise<StorefrontDesign> => {
    const r = await apiClient.put<{ result: StorefrontDesign }>('/owner-site', design, {
      params: ownerId ? { ownerId } : undefined,
    });
    return r.data?.result ?? design;
  },

  /**
   * Upload a single design asset (logo/favicon/banner/background/video) and return its public URL.
   *
   * Preferred path: ask the API for a short-lived write SAS, then PUT the file DIRECTLY to Azure
   * Blob — the API and Node proxy never touch the bytes, so size is bounded only by the blob
   * service. Falls back to the server-side multipart upload only when the SAS endpoint itself is
   * unavailable (e.g. not yet deployed), so this keeps working during rollout.
   *
   * @param onProgress optional 0–100 upload-percent callback (direct path only).
   */
  uploadAsset: async (
    file: File,
    ownerId?: string,
    onProgress?: (percent: number) => void,
  ): Promise<string> => {
    let target: { uploadUrl: string; blobUrl: string } | null = null;
    try {
      const r = await apiClient.post<{ result: { uploadUrl: string; blobUrl: string } }>(
        '/owner-site/upload-url',
        { fileName: file.name, contentType: file.type || 'application/octet-stream' },
        { params: ownerId ? { ownerId } : undefined },
      );
      target = r.data?.result ?? null;
    } catch (e) {
      // SAS endpoint unavailable (not deployed / 503) — fall back to server-side upload below.
      console.warn('upload-url unavailable, falling back to server upload:', e);
    }

    if (target?.uploadUrl && target?.blobUrl) {
      // Direct-to-blob PUT. Bare axios (no apiClient): the SAS URL is pre-authorized and must NOT
      // carry our bearer token or baseURL. x-ms-blob-type is required by the Put Blob REST API.
      await axios.put(target.uploadUrl, file, {
        headers: {
          'x-ms-blob-type': 'BlockBlob',
          'Content-Type': file.type || 'application/octet-stream',
        },
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
        onUploadProgress: onProgress
          ? (e) => { if (e.total) onProgress(Math.round((e.loaded / e.total) * 100)); }
          : undefined,
      });
      return target.blobUrl;
    }

    // Fallback: stream through the API (multipart).
    const form = new FormData();
    form.append('file', file);
    const r = await apiClient.post<{ result: { url: string } }>('/owner-site/upload', form, {
      params: ownerId ? { ownerId } : undefined,
      onUploadProgress: onProgress
        ? (e) => { if (e.total) onProgress(Math.round((e.loaded / e.total) * 100)); }
        : undefined,
    });
    const url = r.data?.result?.url;
    if (!url) throw new Error('Upload did not return a URL');
    return url;
  },

  /** Translate one text into a single target language (Claude-backed). */
  translateText: async (text: string, targetLanguage: string, sourceLanguage?: string): Promise<string> => {
    const r = await apiClient.post<any>('/translation/translate', { text, targetLanguage, sourceLanguage });
    const data = r.data?.result ?? r.data;
    if (data?.translation) return data.translation as string;
    throw new Error('Invalid response from translation API');
  },

  /**
   * Start the server-side background translation job: detects the source language and fills every
   * EMPTY target language across the storefront content + design text. The GUI sends its current
   * language list as targets, so newly-added languages are covered automatically. Poll
   * {@link getTranslationStatus} for progress.
   */
  startTranslation: async (
    ownerId: string | undefined,
    siteLanguage: string,
    targets: { code: string; name: string }[],
  ): Promise<TranslationJobStatus> => {
    const r = await apiClient.post<{ result: TranslationJobStatus }>(
      '/owner-site/translate',
      { siteLanguage, targets },
      { params: ownerId ? { ownerId } : undefined },
    );
    return r.data?.result;
  },

  /**
   * Add a language to the owner's storefront: registers it as a site language (so the storefront
   * language switcher offers it immediately) and starts the background job that translates the whole
   * site — content, design text AND car-category names — into it. Poll {@link getTranslationStatus}.
   */
  addLanguage: async (
    ownerId: string | undefined,
    lang: { code: string; name: string; nativeName?: string; flag?: string },
    siteLanguage?: string,
  ): Promise<TranslationJobStatus> => {
    const r = await apiClient.post<{ result: TranslationJobStatus }>(
      '/owner-site/languages',
      { code: lang.code, name: lang.name, nativeName: lang.nativeName, flag: lang.flag, siteLanguage },
      { params: ownerId ? { ownerId } : undefined },
    );
    return r.data?.result;
  },

  /** Current progress of the owner's storefront translation job. */
  getTranslationStatus: async (ownerId?: string): Promise<TranslationJobStatus> => {
    const r = await apiClient.get<{ result: TranslationJobStatus }>('/owner-site/translate/status', {
      params: ownerId ? { ownerId } : undefined,
    });
    return r.data?.result;
  },
};
